"use client";

import { getAccountDisplayText } from "@/app/layouts/standard/header/accountMenuV2";
import HeaderIcon from "@/app/layouts/standard/header/headerIcon";
import AlgoliaSearchField from "@/app/layouts/standard/header/search-bar/algoliaSearchField";
import SupportTooltip from "@/app/layouts/standard/header/supportTooltip";
import { loadZendesk } from "@/app/layouts/standard/header/utils/zendeskUtils";
import { setOpenMenu } from "@/store/drawerMenuOpenSlice";
import { AppState } from "@/store/store";
import { useGetUserNameDetailsQuery, useGetUserQuery } from "@/store/userApi";
import { dismissTooltip } from "@/store/zendeskNotificationSlice";
import Link from "next/link";
import SupportSVG from "public/next-public/common/icons/support-icon.svg";
import AccountSVG from "public/next-public/common/icons/user-icon.svg";
import Logo from "public/next-public/common/net32-logo.svg";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ShoppingCartSummaryV2 from "./shoppingCartSummaryV2";
import style from "./standardPageHeader.module.scss";

interface StandardPageHeaderProps {
  hideSearch?: boolean;
  fullWidth?: boolean;
}

export default function StandardPageHeader({ hideSearch, fullWidth = false }: StandardPageHeaderProps) {
  const dispatch = useDispatch();
  const { data: user, isLoading: userIsLoading } = useGetUserQuery();
  const { data: nameData, isLoading: nameDataIsLoading } = useGetUserNameDetailsQuery();
  const isGuest = !user || user.IsGuestUser === true;

  // isMounted prevents a hydration mismatch: on the client, RTK Query data may already be cached
  // from prior navigation, causing isLoading to be false at hydration time. The server always
  // SSRs with isLoading true (no cache), so we force the loading state until after mount.
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const accountIconLoading = !isMounted || userIsLoading || (!!user && !user.IsGuestUser && nameDataIsLoading);
  const openMenu = useSelector((state: AppState) => state.drawerMenuOpenSlice.openMenu);
  const isZendeskChatHidden = useSelector((state: AppState) => state.zendeskNotificationSlice.chatHidden);
  const isTooltipDismissed = useSelector((state: AppState) => state.zendeskNotificationSlice.tooltipDismissed);
  const zendeskLoaded = useSelector((state: AppState) => state.zendeskNotificationSlice.zendeskLoaded);

  const showTooltip = zendeskLoaded && isZendeskChatHidden && !isTooltipDismissed;

  const handleDismissTooltip = () => {
    dispatch(dismissTooltip());
  };

  useEffect(() => {
    dispatch(setOpenMenu("none"));
    // only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set up Zendesk messenger event listeners to detect close/minimize
  useEffect(() => {
    // Wrapped in a function to avoid race condition where the Zendesk widget
    // is loaded after the component is unmounted
    const loadAndShowZendesk = async () => {
      if (!zendeskLoaded && !isZendeskChatHidden) {
        await loadZendesk(dispatch, false);
      }
      if (isZendeskChatHidden) {
        window.zE?.("messenger", "hide");
      } else {
        window.zE?.("messenger", "show");
        window.zE?.("messenger", "open");
      }
    };

    loadAndShowZendesk();

    // re-run if zendesk gets loaded after render
  }, [dispatch, isZendeskChatHidden, zendeskLoaded]);

  const handleToggleMenu = (menu: "account" | "support" | "cart") => {
    if (menu === openMenu) {
      dispatch(setOpenMenu("none"));
    } else {
      dispatch(setOpenMenu(menu));
    }
  };

  return (
    <header id="header" className={`${style.header} ${hideSearch ? style.hideSearch : ""}`}>
      <div className={`${style.headerContent} ${fullWidth ? style.fullWidth : ""}`}>
        <Link className={style.headerLogo} href="/">
          <Logo width="123" height="34" />
        </Link>
        {!hideSearch && (
          <div className={style.search}>
            <AlgoliaSearchField />
          </div>
        )}
        <div className={style.headerActions}>
          <HeaderIcon
            className={style.account}
            text={isGuest ? "Sign In" : getAccountDisplayText(nameData, true)}
            onClick={() => handleToggleMenu("account")}
            activeMenu={openMenu === "account"}
            isLoading={accountIconLoading}
            menuTrigger="account"
          >
            <AccountSVG className={style.headerIcon} />
          </HeaderIcon>
          <div className={style.supportWrapper}>
            <HeaderIcon
              className={style.support}
              text={"Support"}
              onClick={() => handleToggleMenu("support")}
              activeMenu={openMenu === "support"}
              menuTrigger="support"
              // TODO: show notification in later story
              // showNotification={isZendeskChatHidden}
            >
              <SupportSVG className={style.headerIcon} />
            </HeaderIcon>
            {showTooltip && <SupportTooltip onDismiss={handleDismissTooltip} />}
          </div>
          <ShoppingCartSummaryV2 />
        </div>
      </div>
    </header>
  );
}
